// apps/web/src/components/shell/sidebar-badge.tsx — Fase 17.8, Plano 13 (D-15; UI-SPEC U-11).
// Badge de pendência da sidebar. Variante número (menu expandido e flyout): 20px, "99+" acima de 99, aria-label com a contagem real
// no plural certo. Variante ponto (rail): 8px com anel de 2px da cor do menu; a contagem vai no tooltip do item. Não renderiza com 0.
// O pulso é um anel (::after) que cresce e some em 240 ms, UMA vez, e só quando a contagem muda e quem chama pediu (`pulsar`):
// a primeira renderização e o retorno da aba não pulsam. Sempre montado pelo pai (o componente some sozinho com 0), para uma
// contagem que sai de 0 também contar como mudança.
import { useState, type ReactElement } from 'react';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import { rotuloAcessivel, rotuloBadge } from '../../lib/painel/pendencias';

export interface SidebarBadgeProps {
  /** Id do módulo na navegação (grupo ou subitem): compõe o `data-testid`. */
  readonly modulo: string;
  readonly contagem: number;
  readonly variante?: 'numero' | 'ponto';
  /** Esta mudança de contagem (se houver) merece o pulso? Decidido por `devePulsar` no hook das pendências. */
  readonly pulsar?: boolean;
}

export function SidebarBadge({ modulo, contagem, variante = 'numero', pulsar = false }: SidebarBadgeProps): ReactElement | null {
  const reduzido = usePrefersReducedMotion();
  const [contagemAnterior, definirContagemAnterior] = useState(contagem);
  const [pulsando, definirPulsando] = useState(false);
  // Estado derivado na renderização: o pulso começa no mesmo quadro em que a contagem muda e termina em onAnimationEnd.
  if (contagem !== contagemAnterior) {
    definirContagemAnterior(contagem);
    definirPulsando(pulsar && !reduzido);
  }

  const rotulo = rotuloBadge(contagem);
  if (rotulo === null) return null;

  const classes = ['sidebar-badge', variante === 'ponto' ? 'sidebar-badge--ponto' : '', pulsando ? 'sidebar-badge--pulso' : '']
    .filter((classe) => classe !== '')
    .join(' ');
  return (
    <span
      role="img"
      aria-label={rotuloAcessivel(contagem)}
      data-testid={`sidebar-badge-${modulo}`}
      data-contagem={contagem}
      className={classes}
      onAnimationEnd={() => definirPulsando(false)}
    >
      {variante === 'numero' ? <span className="num" aria-hidden="true">{rotulo}</span> : null}
    </span>
  );
}
